import { useRouter } from 'next/router';
import { useCallback, useState } from 'react';
import { BsBookmarkCheckFill, BsBookmarkPlus } from 'react-icons/bs';
import { IoClose } from 'react-icons/io5';

import { ActionIcon, ActionIconProps, Button, ButtonProps, Tooltip } from '@mantine/core';
import { CollectionHelpers } from 'src/helpers/CollectionHelpers';
import { useSnapshot } from 'valtio';

import { CompanyBase, QrScanType, SavedItemType } from 'models';

import { ShowtimeButton } from 'components/showtime/components/ShowtimeButton';
import { useAction } from 'hooks/use-action';
import { useCurrentSavedItems } from 'hooks/use-current-saved-items';
import { useCurrentUser } from 'hooks/use-current-user';
import { useLocalEvent } from 'hooks/use-local-event';
import { LocalNotificationService } from 'services/LocalNotificationService';
import { ModalService } from 'services/ModalService';
import { SavedItemsService } from 'services/SavedItemsService';
import { showtimeState } from 'state/showtime';

import cx from './SaveButton.module.scss';

export const SAVE_ITEM_COPY = {
    default: {
        default: {
            label: 'Bookmark',
            message: null,
            icon: <BsBookmarkPlus />,
        },
        save: {
            label: 'Bookmarked',
            message: 'Saved to My Bookmarks',
            icon: <BsBookmarkCheckFill />,
        },
        remove: {
            label: 'Remove',
            message: 'Removed from My Bookmarks',
            icon: <IoClose />,
        },
    },
    [SavedItemType.PROFILE]: {
        default: {
            label: 'Bookmark',
            message: null,
            icon: <BsBookmarkPlus />,
        },
        save: {
            label: 'Bookmarked',
            message: 'Profile saved to My Bookmarks',
            icon: <BsBookmarkCheckFill />,
        },
        remove: {
            label: 'Remove',
            message: 'Profile removed from My Bookmarks',
            icon: <IoClose />,
        },
    },
};

type Props = {
    id: string;
    name: string;
    type: SavedItemType;
    iconOnly?: boolean;
    buttonProps?: ButtonProps;
    actionIconProps?: ActionIconProps;
    // The exhibitor the item belongs to. While they or the visitor are at an event, saving goes to the visitor's
    // RE+ Showtime instead of their bookmarks.
    company?: CompanyBase | CompanyBase['id'] | null;
} & ButtonProps;

const SaveButton = ({ id, name, type, iconOnly, buttonProps, actionIconProps }: Props) => {
    const copy = type in SAVE_ITEM_COPY ? SAVE_ITEM_COPY[type as keyof typeof SAVE_ITEM_COPY] : SAVE_ITEM_COPY.default;

    const router = useRouter();
    const user = useCurrentUser();

    const {
        lookup: { [id]: savedItemId },
    } = useCurrentSavedItems();

    const [justSaved, setJustSaved] = useState(false);

    const state = savedItemId ? 'save' : 'default';

    const saveItem = useCallback(async () => {
        if (!user) return;

        const payload = {
            user: user.id,
            item: {
                value: id,
                relationTo: type,
            },
        };

        if (savedItemId) {
            await SavedItemsService.delete(savedItemId, payload);

            LocalNotificationService.showSuccess({
                message: `${copy.remove.message}`,
            });

            setJustSaved(false);
        } else {
            await SavedItemsService.create(payload);

            LocalNotificationService.showSuccess({
                message: `${copy.save.message}`,
                actions: [
                    {
                        label: 'View all',
                        onClick: SavedItemsService.navigate.overview,
                    },
                ],
            });

            setJustSaved(true);
        }
    }, [id, type, user, state, savedItemId, copy.save.message, copy.remove.message]);

    const openLogin = () => {
        let redirect = router.asPath.split('?')[0];

        const searchParams = new URLSearchParams(window.location.search);

        searchParams.set('action', 'save-item');
        searchParams.set('item', id);
        searchParams.set('saveType', type);

        redirect += '?' + searchParams.toString();

        if (window.location.hash) {
            redirect += window.location.hash;
        }

        ModalService.openLoginModal({
            title: `Add "${name}" to My Bookmarks`,
            message: '',
            redirect,
        });
    };

    const [toggleSave, saving] = useAction(async () => {
        if (!user) {
            openLogin();
            return;
        }

        saveItem();
    });

    const getColor = () => {
        if (savedItemId) {
            return 'green';
        }

        return 'black';
    };

    const props = {
        'color': getColor(),
        'variant': justSaved ? 'filled' : 'outline',
        'onClick': toggleSave,
        'disabled': saving,
        'data-saved': !!savedItemId,
        'style': {
            transition: 'background-color 100ms ease',
        },
    };

    const { label, icon } = copy[state];

    if (iconOnly) {
        return (
            <Tooltip label={label}>
                <ActionIcon
                    {...props}
                    size="lg"
                    radius={99}
                    c={getColor()}
                    variant="white"
                    className={`${cx.root} ${cx.actionIcon}`}
                    {...actionIconProps}
                    style={{
                        boxShadow: 'var(--mantine-shadow-xs)',
                        border: '1px solid var(--mantine-color-gray-0)',
                        ...(actionIconProps?.style || {}),
                    }}
                >
                    {icon}
                </ActionIcon>
            </Tooltip>
        );
    }

    return (
        <Button className={cx.root} size="compact-xs" leftSection={icon} {...props} {...buttonProps}>
            {label}
        </Button>
    );
};

const WrappedSaveButton = (props: Props) => {
    const { showShowtimeAndActiveEvent } = useSnapshot(showtimeState);
    const { localEvent, isLoading: localEventLoading } = useLocalEvent();

    // Only RE+ visitors at the event save to RE+ Showtime; everyone else keeps their regular bookmarks
    const companyId = CollectionHelpers.id(props.company);
    const isCompanyInEvent = localEvent?.companies?.some((exhibitor) => exhibitor.company === companyId);

    const scanType = {
        [SavedItemType.PROFILE]: QrScanType.PROFILE,
        [SavedItemType.COMPONENT]: QrScanType.PRODUCT,
        [SavedItemType.COLLATERAL]: QrScanType.COLLATERAL,
        [SavedItemType.PROJECT]: undefined,
    }[props.type];

    if (!props.company || !scanType) {
        return <SaveButton {...props} />;
    }

    // Until the visitor's event is known, either button could be the right one, and a click could save to the wrong list
    if (localEventLoading) {
        return null;
    }

    if (!showShowtimeAndActiveEvent || !isCompanyInEvent) {
        return <SaveButton {...props} />;
    }

    return (
        <ShowtimeButton
            company={props.company}
            type={scanType}
            itemId={props.id}
            title={props.name}
            iconOnly={props.iconOnly}
            buttonProps={props.buttonProps}
            actionIconProps={props.actionIconProps}
            fallback={<SaveButton {...props} />}
        />
    );
};

export { WrappedSaveButton as SaveButton };
